/**
 * Template definitions for client-saas.
 *
 * Definitions live in the app because they reach app services (REST, auth) and
 * app-owned web components; the registry mechanism itself stays in
 * shared/meta/meta_templates.js so client-sse-console can adopt it later.
 *
 * Registering here does four things at once - see the registry header. The two
 * that are easy to get wrong:
 *
 *   `when`       goes verbatim into the system prompt. Write it as the condition
 *                you would give a colleague, not as a title.
 *   `transcript` is the ONLY thing the model learns about this view. Keep it
 *                short and factual; it costs context on every following turn.
 *
 * Escape every interpolated value with esc() / attr(). This markup is inserted
 * trusted, so a template is the one place where an unescaped value matters.
 */

import { registerTemplate, esc, attr } from '../../../../shared/meta/meta_templates.js';
import '../../../../shared/wc/m5t-vcr-controls.js';
import '../../../../shared/wc/ether5/ether5Grid.js';
import '../../../../shared/wc/ether5/ether5Card.js';
import { getRoleByName } from '../../../../packages/shared/src/metadata/entityClasses.js';
import coreRest from '../core/corerest_api.js';
import { appCaption, appTitle, appDesc, appShape, appHost, ownedBySignedIn } from './etherApps.js';
import { setApps, getApps, resolveByName } from '../services/appsCache.js';

/** Same bar the `::` CLI uses - a debugging surface is an admin affordance. */
const MIN_DEBUG_ROLE = 50;

function roleValue() {
  const entity = window.AuthController?.getEntity?.();
  if (!entity?.class) return 0;
  if (typeof entity.class === 'object') return entity.class.value ?? 0;
  return getRoleByName(entity.class)?.value ?? 0;
}

/**
 * EXAMPLE - replace with real views.
 *
 * Deliberately fetch-free so the whole chain (routing -> hydrate -> canvas ->
 * affordance) can be exercised with `::template render ACCOUNT_SUMMARY` before
 * any backend work exists.
 */
registerTemplate({
  id: 'ACCOUNT_SUMMARY',
  when: 'the user asks about their own account, profile, role, or plan',

  patterns: [/^(?!.*\?)(?=.*\b(my|me|account|profile)\b).*\b(account|profile)\b/i],

  render: () => {
    const entity = window.AuthController?.getEntity?.();

    if (!entity) {
      return '<p>No account is signed in.</p>';
    }

    const rows = [
      ['Name', `${entity.first ?? ''} ${entity.last ?? ''}`.trim()],
      ['Email', entity.email],
      ['Company', entity.company || '—'],
      ['Role', entity.class],
      ['Status', entity.status]
    ];

    return `
      <h3>Account</h3>
      <div class="table-scroll">
        <table>
          <tbody>
            ${rows.map(([label, value]) => `
              <tr>
                <th scope="row">${esc(label)}</th>
                <td>${esc(value)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
      <p>
        <a data-n55-prompt="What can I do with the ${esc(entity.class)} role?">what this role allows</a>
        &nbsp;·&nbsp;
        <a data-n55-prompt="How do I change my password?">change password</a>
      </p>
    `;
  },

  transcript: () => {
    const entity = window.AuthController?.getEntity?.();
    return entity
      ? `Showed the user their account summary: ${entity.email}, role ${entity.class}, status ${entity.status}. They can see it on screen.`
      : 'Tried to show an account summary, but nobody is signed in.';
  }
});

/**
 * Time-travel debugging - transport controls for the PubSub tape recorder.
 *
 * The POC for the interactive half of the contract. It proves the two things a
 * markdown bubble cannot do:
 *
 *   - the markup instantiates a real web component (<m5t-vcr-controls>), which
 *     the parser upgrades on insert and which owns its own state and events;
 *   - the affordance links below it fire new conversation turns, so the view is
 *     a place the conversation continues from rather than a dead end.
 *
 * The controls talk to the recorder directly through its window hooks. Routing
 * them through PubSub would put every button press onto the tape being recorded.
 */
registerTemplate({
  id: 'VCR_TIME_TRAVEL',
  // Tightened from a much longer line. The original enumerated every synonym it
  // could ("time-travel", "replay", "tape", "reproduce a bug"), which gave it the
  // widest lexical surface in the catalog and made it the default landing spot -
  // "what time is it" matched on the word "time" alone. A narrow `when` that
  // demands an explicit request routes better than an exhaustive one.
  when: 'the user explicitly asks to record or replay this session with the event tape recorder, or to time-travel debug',

  patterns: [/\b(vcr|time[- ]?travel|tape recorder)\b/i, /\b(record|replay)\b.*\bsession\b/i],

  render: () => {
    if (roleValue() < MIN_DEBUG_ROLE) {
      return '<p>Time-travel debugging needs an administrator role.</p>';
    }

    return `
      <h3>Time Travel</h3>
      <p>
        Record this session's event timeline, save it as JSON, and replay it here or
        in another browser.
      </p>

      <m5t-vcr-controls></m5t-vcr-controls>

      <p>
        <a data-n55-prompt="What gets captured on a VCR tape, and what does not?">what gets captured</a>
        &nbsp;·&nbsp;
        <a data-n55-prompt="How do I send a VCR tape to support?">send a tape to support</a>
        &nbsp;·&nbsp;
        <a data-n55-prompt="Why is replaying a tape with AUTH events dangerous?">why skip AUTH/LLM</a>
      </p>
    `;
  },

  transcript: () => {
    if (roleValue() < MIN_DEBUG_ROLE) {
      return 'The user asked to time-travel debug, but their role does not allow it. Nothing was shown.';
    }
    return 'Showed the user the time-travel debugging panel: transport controls for the PubSub tape ' +
      'recorder (record, stop, save, load, play, cancel) with speed, dry-run, and skip-AUTH/LLM options. ' +
      'It is on screen and working - do not repeat the button list back to them.';
  }
});

/**
 * STUB - the general landing view, and deliberately the catch-all.
 *
 * A small model under a closed enum would rather pick something than decline, so
 * whichever entry has the broadest `when` absorbs the uncertainty. That used to
 * be VCR_TIME_TRAVEL by accident, which is how "dashboard", "engagements" and
 * "hackathon" all landed on the tape recorder. This entry claims that role on
 * purpose: if the router is going to guess, it should guess somewhere harmless
 * and plausible.
 *
 * Registered LAST so it sits closest to NONE in the catalog, where a
 * position-biased model is most likely to look.
 *
 * Replace the placeholder tiles with real data - the shape is a wired example,
 * not a design.
 */
registerTemplate({
  id: 'DASHBOARD',
  when: 'the user asks about their data, engagements, apps, activity, metrics, or anything else inside the application that the other views do not specifically cover - this is the general landing view',

  // Claiming the nav token is the entire wiring. Clicking Home > Dashboard speaks
  // `navPrompt` for the user, so the exchange reads as a conversation and the
  // transcript gets a real question paired with the answer.
  nav: 'HOME_DASH',
  navPrompt: 'Show me the dashboard',

  patterns: [/\bdash(board)?\b/i],

  render: () => {
    const entity = window.AuthController?.getEntity?.();
    const who = entity?.first ? `, ${esc(entity.first)}` : '';

    // Placeholder figures. Swap for a fetch + `source` expression when there is
    // an endpoint behind this.
    const tiles = [
      ['Engagements', '—'],
      ['Active apps', '—'],
      ['Messages', '—']
    ];

    return `
      <h3>Dashboard</h3>
      <p>Welcome back${who}. This view is a stub - the tiles are not wired to data yet.</p>

      <div class="table-scroll">
        <table>
          <thead>
            <tr>${tiles.map(([label]) => `<th scope="col">${esc(label)}</th>`).join('')}</tr>
          </thead>
          <tbody>
            <tr>${tiles.map(([, value]) => `<td>${esc(value)}</td>`).join('')}</tr>
          </tbody>
        </table>
      </div>

      <p>
        <a data-n55-prompt="Show me my account">account</a>
        &nbsp;·&nbsp;
        <a data-n55-prompt="What can I do from here?">what can I do here</a>
      </p>
    `;
  },

  transcript: () => 'Showed the user the dashboard, which is currently a stub with placeholder tiles ' +
    'and no real figures. Do not invent numbers for it; say it is not wired up yet if asked.'
});

const ETHER5_GAP = 24;

/**
 * Live sseEtherSubscriberApp rows, rendered as an ether5 quilt.
 *
 * The entity has no display columns, so every visible field is derived in
 * etherApps.js - see that file for the mapping and its caveats.
 */
registerTemplate({
  id: 'MY_APPS',
  when: 'the user asks about their apps, widgets, cards, or what is installed in their account',

  nav: 'HOME_APPS',
  navPrompt: 'Show me my apps',

  // Deterministic first pass. These are the phrasings people actually type; the
  // model never sees them. Negative lookahead keeps "create an app" out.
  patterns: [
    /^(?!.*\b(create|add|new|register|install|snippet|embed)\b).*\bapps?\b/i
  ],

  actions: {
    /**
     * "Create a new app named HVAC WIKI, with the URL https://whatever.com"
     *
     * The same directive a New button would publish, so the conversational and
     * click paths converge before anything acts - see [[conversation-contract]].
     */
    CREATE_APP: {
      when: 'the user wants to add, create, or register a new app, usually giving a name and a URL',
      needs: 'To add an app I need a name and a fully qualified URL.',

      patterns: [/\b(create|add|register|new)\b[^.]*\bapp\b/i],

      params: {
        name: { type: 'string', required: true },
        url: { type: 'url', required: true }
      },

      run: async ({ name, url }) => {
        // /mine, not POST /. Owner, guid and status are set server-side from the
        // token there; POST / accepts whatever it is handed and stays available
        // for the admin applet, which legitimately creates for other owners.
        const created = await coreRest.post('/m5t/v5/sseEtherSubscriberApp/mine', { name, url });
        if (!created.ok) {
          throw new Error(created.data?.message || created.data?.error || 'create failed');
        }

        const guid = created.data?.data?.guid;

        // The app is DRAFT until its domain answers 200. Failure here is not a
        // failure of the create - the app exists and can be verified later - so
        // it must not throw and roll the user back into an error bubble.
        let verified = null;
        try {
          const res = await coreRest.post(`/m5t/v5/sseEtherSubscriberApp/mine/${guid}/verify`, {});
          verified = res.data;
        } catch (err) {
          console.warn('[CREATE_APP] verification did not run', err);
        }

        if (verified && !verified.verified) {
          console.info(`[CREATE_APP] ${name} stays DRAFT: ${verified.reason}`);
        }

        // The list just changed. runWorkflow re-renders MY_APPS immediately after
        // this returns, and that render refreshes the cache from the fetch - so
        // the model's view is current by the time the next prompt is composed.
        return { created: created.data, verified };
      }
    }
  },

  // Owner-scoped server-side from the JWT, and without app_keys in the
  // projection. The unscoped GET / returns every tenant's rows.
  fetch: () => coreRest.get('/m5t/v5/sseEtherSubscriberApp/mine'),

  // corerest wraps the body: { ok, status, data: { ok, count, data: [...] } }
  source: 'data.data',

  render: (rows) => {
    // The render is the one place the full list is reliably in hand, so it is
    // where the model's living view gets refreshed. Scoped rows only - the cache
    // feeds a prompt, and must never carry another tenant's app even briefly.
    // Belt and braces. The endpoint already scopes, so this should be a no-op -
    // if it ever removes a row, the server-side filter has regressed.
    const guid = window.AuthController?.getEntity?.()?.guid;
    const apps = ownedBySignedIn(rows, guid);
    setApps(apps);

    if (!apps.length) {
      return `
        <h3>My Apps</h3>
        <p>No apps yet.</p>
        <p><a data-n55-prompt="How do I add a new app?">add your first app</a></p>
      `;
    }

    return `
      <h3>My Apps</h3>
      <ether5-grid data-ether5-gap="${ETHER5_GAP}">
        ${apps.map((app, i) => `
          <ether5-card
            data-ether5-shape="${attr(appShape(i))}"
            data-ether5-caption="${attr(appCaption(app))}"
            data-ether5-title="${attr(appTitle(app))}"
            data-ether5-desc="${attr(appDesc(app))}"
            data-ether5-token="${attr(app.guid ?? '')}"></ether5-card>
        `).join('')}
      </ether5-grid>
      <p>
        <a data-n55-prompt="Which of my apps get the most engagement?">which get the most engagement</a>
        &nbsp;·&nbsp;
        <a data-n55-prompt="How do I add a new app?">add a new app</a>
      </p>
    `;
  },

  transcript: (rows) => {
    const guid = window.AuthController?.getEntity?.()?.guid;
    const apps = ownedBySignedIn(rows, guid);

    if (!apps.length) return 'Showed the user their apps: they have none yet.';

    return `Showed the user their ${apps.length} app card(s): ` +
      apps.map((a) => appTitle(a)).join(', ') +
      '. Only names, domains, last-modified dates and lifecycle status are on screen - no usage or ' +
      'engagement figures. An app shows DRAFT until its domain answers 200 to a verification ping.';
  }
});

/** Where a customer's page loads the SDK from. */
const SNIPPET_BASE = 'https://streamsyncengage-saas.onrender.com/m5t/v5/coreSnippet';

const installSnippet = (guid) =>
  `<script src="${SNIPPET_BASE}?appguid=${guid}&token=INSTALL_MVV_SSETHER" async></script>`;

/**
 * The install snippet for one app.
 *
 * A VIEW, not an action - it shows something and mutates nothing. An action would
 * run and then re-render its owning view, which for this would replace the very
 * thing the user asked to see.
 *
 * The name is resolved against appsCache, never by the model. The model reads the
 * living view in its context, repeats a name back, and JS turns that into a guid
 * from the same list it rendered. A guid the model invented could address another
 * tenant's app; a name it invented simply fails to match.
 */
registerTemplate({
  id: 'APP_SNIPPET',
  when: 'the user asks for the installation snippet, embed code, script tag, or how to install one of their apps',

  patterns: [/\b(snippet|embed code|script tag|install(ation)?)\b/i],

  params: { name: { type: 'string', required: true } },

  render: (_data, params) => {
    const resolved = resolveByName(params.name);

    if (!resolved) {
      const known = getApps().map((a) => appTitle(a));
      return `
        <h3>Install Snippet</h3>
        <p>I could not find an app called “${esc(params.name)}”.</p>
        ${known.length
          ? `<p>You have: ${known.map((n) => esc(n)).join(', ')}.</p>`
          : '<p><a data-n55-prompt="Show me my apps">load your apps first</a></p>'}
      `;
    }

    if (resolved.ambiguous) {
      return `
        <h3>Install Snippet</h3>
        <p>“${esc(params.name)}” matches more than one app — which did you mean?</p>
        <p>${resolved.ambiguous.map((a) =>
          `<a data-n55-prompt="${attr(`Give me the installation snippet for the app named ${appTitle(a)}`)}">${esc(appTitle(a))}</a>`
        ).join(' &nbsp;·&nbsp; ')}</p>
      `;
    }

    const app = resolved.app;
    const draft = app.status !== 'ENABLED';

    return `
      <h3>Install Snippet — ${esc(appTitle(app))}</h3>
      <p>Paste this just before <code>&lt;/body&gt;</code> on ${esc(appHost(app) || 'your site')}.</p>
      <pre id="snippet-code">${esc(installSnippet(app.guid))}</pre>
      <p>
        <button type="button" data-n55-copy="snippet-code">Copy</button>
      </p>
      ${draft ? `
        <p><strong>This app is ${esc(app.status)}.</strong> The snippet returns
        <code>// App is not enabled</code> until its domain verifies, so installing it now
        will not load.</p>` : ''}
      <p>
        <a data-n55-prompt="Show me my apps">back to my apps</a>
      </p>
    `;
  },

  transcript: (_data, params) => {
    const resolved = resolveByName(params.name);
    if (!resolved) return `The user asked for an install snippet for "${params.name}", which matched no app. Nothing was shown.`;
    if (resolved.ambiguous) return `"${params.name}" matched several apps; asked the user which one.`;
    return `Showed the install snippet for the app "${appTitle(resolved.app)}" - a script tag they can copy. ` +
      'Do not repeat the snippet or the app guid back to them; it is on screen.';
  }
});

console.log('[Templates] Registered');
